import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useApi } from '@/utils/axios'
import { MENSAGENS_CLIENTE_ROTAS } from '@/utils/apiRoutes'

export const useMensagensClienteStore = defineStore('mensagensCliente', () => {
  // state
  const items = ref([])
  const meta = reactive({
    search: null,
    current_page: 1,
    total: 0,
    per_page: 100,
    last_page: 1,
  })
  const loading = ref(false)
  const error = ref(null)

  // filtros padrão
  const filters = reactive({
    status: null,
    cliente_nome: null,
    cliente_email: null,
    cliente_telefone1: null,
    init_created_at: null,
    end_created_at: null,
    per_page: 100,
    page: 1,
  })

  // computed helpers
  const total = computed(() => meta.total)
  const currentPage = computed(() => meta.current_page)
  const lastPage = computed(() => meta.last_page)
  const perPage = computed(() => meta.per_page)

  // fetch com parâmetros (usa filtros reativos por padrão)
  async function fetchMensagens(options = {}) {
    loading.value = true
    error.value = null

    const params = {
      status: options.status,
      cliente_nome: options.cliente_nome,
      cliente_email: options.cliente_email,
      cliente_telefone1: options.cliente_telefone1,
      init_created_at: options.init_created_at,
      end_created_at: options.end_created_at,
      per_page: options.per_page,
      page: options.page,
    }

    try {
      const response = await useApi(MENSAGENS_CLIENTE_ROTAS.LISTAR).fetch({ params })
      // backend: { status, message, data: [...], meta: { current_page, total, per_page, last_page } }
      items.value = Array.isArray(response.data) ? response.data : []
      if (response.meta) {
        meta.search = response.meta.search ?? null
        meta.current_page = response.meta.current_page ?? params.page ?? 1
        meta.total = response.meta.total ?? 0
        meta.per_page = response.meta.per_page ?? params.per_page ?? 100
        meta.last_page = response.meta.last_page ?? 1
      }
      // persistir últimos filtros/pagina usados
      filters.status = params.status ?? null
      filters.cliente_nome = params.cliente_nome ?? null
      filters.cliente_email = params.cliente_email ?? null
      filters.cliente_telefone1 = params.cliente_telefone1 ?? null
      filters.init_created_at = params.init_created_at ?? null
      filters.end_created_at = params.end_created_at ?? null
      filters.per_page = params.per_page ?? filters.per_page
      filters.page = params.page ?? filters.page
      return { success: true, data: items.value, meta }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao carregar mensagens'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // enviar observação/resposta e atualizar listagem automaticamente
  async function enviarResposta(mensagemId, texto) {
    if (!mensagemId) {
      return { success: false, error: 'ID da mensagem não informado' }
    }
    loading.value = true
    error.value = null
    try {
      // payload: ajuste conforme backend; aqui usamos { observation: texto }
      const response = await useApi(MENSAGENS_CLIENTE_ROTAS.ENVIAR_RESPOSTA(mensagemId)).put({
        observation: texto,
      })

      // após sucesso, refetch com os filtros/página atuais
      await fetchMensagens({
        status: filters.status,
        cliente_nome: filters.cliente_nome,
        cliente_email: filters.cliente_email,
        cliente_telefone1: filters.cliente_telefone1,
        init_created_at: filters.init_created_at,
        end_created_at: filters.end_created_at,
        per_page: filters.per_page,
        page: filters.page,
      })

      return { success: true, data: response.data ?? null }
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao enviar resposta'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // utilitários para interagir com filtros/paginação
  function setFilter(partial = {}) {
    Object.assign(filters, partial)
  }

  function setPage(p) {
    filters.page = p
    return fetchMensagens({ page: p })
  }

  function setPerPage(pp) {
    filters.per_page = pp
    filters.page = 1
    return fetchMensagens({ per_page: pp, page: 1 })
  }

  function resetFilters() {
    filters.status = null
    filters.cliente_nome = null
    filters.cliente_email = null
    filters.cliente_telefone1 = null
    filters.init_created_at = null
    filters.end_created_at = null
    filters.per_page = 100
    filters.page = 1

    fetchMensagens({})
  }

  function clearData() {
    items.value = []
    meta.current_page = 1
    meta.total = 0
    meta.per_page = 100
    meta.last_page = 1
    error.value = null
  }

  return {
    // state
    items,
    meta,
    loading,
    error,
    filters,

    // computed
    total,
    currentPage,
    lastPage,
    perPage,

    // actions
    fetchMensagens,
    enviarResposta,
    setFilter,
    setPage,
    setPerPage,
    resetFilters,
    clearData,
  }
})
